"use client";

import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { FaArrowRight, FaGoogle, FaLocationDot, FaPhone, FaStar } from "react-icons/fa6";
import { TbFirstAidKit } from "react-icons/tb";
import { SiGoogle, SiGoogleadsense, SiShopify } from "react-icons/si";
import {
  IoChatbubblesOutline,
  IoHammerSharp,
  IoSparkles,
  IoSparklesSharp,
} from "react-icons/io5";
import { MdMarkEmailRead } from "react-icons/md";
import { LuScrollText } from "react-icons/lu";
import { FaInfoCircle, FaRegHandshake } from "react-icons/fa";
import { FaMoneyBillTrendUp } from "react-icons/fa6";
import { BsPaintBucket } from "react-icons/bs";
import MobileSwitcher from "./MobileSwitcher";

const siteData = {
  service: [
    {
      path: "web-design",
      short: "Web Design",
    },
    {
      path: "vip-support",
      short: "VIP Support",
    },
    {
      path: "email-marketing",
      short: "Email & SMS",
    },
    {
      path: "paid-search",
      short: "Paid Search",
    },
    {
      path: "social-media-marketing",
      short: "Social Media",
    },
    {
      path: "seo",
      short: "SEO",
    },
    {
      short: "Local Listing Management",
      path: "/local-listing-management",
    },
    {
      short: "AI Customer Service Agent",
      path: "/addons/ai-customer-service-agent",
    },
  ],
  single: [
    {
      path: "our-work",
      short: "Our Work",
      title: "Our Portfolio",
      content: {},
    },
    {
      path: "learn",
      short: "Learn",
      title: "Learn from the Experts",
      content: {},
    },
    {
      path: "about",
      short: "About",
      title: "About Ventura",
      content: {},
    },
    {
      path: "about/kevin",
      short: "Kevin",
      title: "Kevin Richards",
      inmenu: false,
      content: {},
    },
    {
      path: "ups",
      short: "UPS",
      title: "Ventura Partners with UPS",
      content: {},
      inmenu: false,
    },
    {
      path: "partners",
      short: "Partners",
      title: "We Partner with the Best",
      content: {},
    },
    {
      path: "Grow",
      short: "Growth Calculator",
      title: "Calculate Your Growth",
      content: {},
    },
  ],
};

const MobileMenu = ({ mobileOpen, setMobileOpen }) => {
  const [icons, setIcons] = useState({
    "Web Design": <BsPaintBucket />,
    "Conversion Rate Optimization": <FaStar />,
    SEO: <FaGoogle />,
    "VIP Support": <TbFirstAidKit />,
    "Paid Search": <SiGoogleadsense />,
    "Social Media": <IoChatbubblesOutline />,
    Email: <MdMarkEmailRead />,
    Learn: <LuScrollText />,
    "Our Work": <IoHammerSharp />,
    Partners: <FaRegHandshake />,
    "Growth Calculator": <FaMoneyBillTrendUp />,
    About: <FaInfoCircle />,
    "AI Domain Generator": <IoSparklesSharp />,
    "BigCommerce to Shopify": <SiShopify />,
    "Custom 404 Pages": <IoSparkles />,
    "AI Customer Service Agent": <IoSparkles />,
    "Local Listing Management": <FaLocationDot />,
  });

  useEffect(() => {
    if (window.innerWidth > 1280) {
      setMobileOpen(false);
    }
  }, [setMobileOpen]);

  const router = useRouter();

  useEffect(() => {
    const closeMobileMenu = () => {
      setMobileOpen(false);
    };

    router.events.on("routeChangeStart", closeMobileMenu);

    return () => {
      router.events.off("routeChangeStart", closeMobileMenu);
    };
  }, [router.events, setMobileOpen]);

  return (
    <>
      {mobileOpen ? (
        <div className="fixed right-2 top-2 z-30 w-10/12 max-w-[500px] xl:hidden">
          <div
            id="mob-menu"
            className="scrollable relative z-[99999] h-[calc(100vh-16px)] overflow-scroll rounded-2xl bg-white !pb-80 shadow-xl ring-1 ring-neutral-900/5"
          >
            <div className="absolute right-2 top-2">
              <MobileSwitcher
                mobileOpen={mobileOpen}
                setMobileOpen={setMobileOpen}
              />
            </div>

            <div className="w-full">
              <div className="rounded-3xl p-10">
                <strong className="block pb-3 leading-none">
                  <span className="mr-1">🙂</span> Hi there!
                </strong>
                <p className="mb-7 text-sm opacity-75">
                  If you need any more information, please feel free to book an
                  appointment. We are happy to answer any questions you may
                  have.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Link href="tel:8135567990">
                    <button
                      type="button"
                      className="flex items-center gap-1.5 rounded-full bg-gradient-to-b from-black/5 to-black/10 px-4 py-2 text-sm font-medium"
                    >
                      <FaPhone /> Call us
                    </button>
                  </Link>
                  <Link href="https://calendly.com/vwd/free-consultation">
                    <button
                      type="button"
                      className="flex items-center gap-1.5 rounded-full bg-gradient-to-b from-black/90 to-black px-4 py-2 text-sm font-medium text-white"
                    >
                      Book now <FaArrowRight />
                    </button>
                  </Link>
                </div>
              </div>

              <div className="w-full border-t">
                <div className="flex w-full items-center">
                  <div className="w-full px-10 pb-5 pt-[34px] text-sm font-medium leading-none tracking-wide opacity-60">
                    Our Services
                  </div>
                </div>
                <div className="px-5">
                  {siteData.service.map((service) => {
                    return (
                      <Link
                        onClick={() => {
                          setMobileOpen(false);
                        }}
                        className="relative flex items-center gap-3 rounded-full px-5 py-3.5 text-sm font-semibold active:bg-gray-100"
                        href={`/${service.path}`}
                        key={service.short}
                      >
                        <span className="text-base opacity-70">
                          {icons[service.short]}
                        </span>

                        {service.short}
                      </Link>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 w-full border-t">
                <div className="flex w-full items-center">
                  <div className="w-full px-10 pb-5 pt-[34px] text-sm font-medium leading-none tracking-wide opacity-60">
                    Our Company
                  </div>
                </div>
                <div className="px-5">
                  {siteData.single.map((service) => {
                    if (service.inmenu === false) return;
                    return (
                      <Link
                        onClick={() => {
                          setMobileOpen(false);
                        }}
                        className="relative flex items-center gap-3 rounded-full px-5 py-3.5 text-sm font-semibold leading-none active:bg-gray-100"
                        href={`/${service.path}`}
                        key={service.short}
                      >
                        <span className="text-base opacity-70">
                          {icons[service.short]}
                        </span>

                        {service.short}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div
            onClick={() => setMobileOpen(false)}
            onKeyUp={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setMobileOpen(false);
              }
            }}
            tabIndex={0}
            role="button"
            className="fixed left-0 top-0 z-[100] h-[calc(100vh)] w-full bg-black/40 xl:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        </div>
      ) : null}
    </>
  );
};

export default MobileMenu;
